import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Message as MessageIcon,
  Book as BookIcon,
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './theme';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ChatMessage from './components/ChatMessage';
import { ChatService } from './services/chatService';
import { Message } from './types/chat';

const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  const [selectedFramework, setSelectedFramework] = useState('');
  const [streamingStats, setStreamingStats] = useState({ chunkCount: 0, lastChunkTime: '' });
  const [renderKey, setRenderKey] = useState(0); // Force re-render counter for streaming updates
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatService = new ChatService();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Debug: Log when component mounts AND set window property
  useEffect(() => {
    console.log('[APP DEBUG] Component mounted at', new Date().toISOString());
    console.log('[APP DEBUG] ChatService initialized');
    (window as any).__CSIPAY_AI_MOUNTED__ = new Date().toISOString();
    console.log('[APP DEBUG] Window property set:', (window as any).__CSIPAY_AI_MOUNTED__);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Store message before clearing input field
    const messageToSend = inputMessage.trim();
    const requestStartTime = Date.now();
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageToSend,
      role: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Create assistant message with "Thinking..." status immediately
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      content: 'Thinking...',
      role: 'assistant',
      timestamp: new Date(),
      status: 'thinking' as const,
      timingInfo: {},
    };

    setMessages(prev => [...prev, assistantMessage]);
    
    // Set timeout to show "Still thinking..." after 2.5 seconds if no response yet
    let stillThinkingTimeout: NodeJS.Timeout | null = null;
    let firstTokenReceived = false;
    let timeToFirstToken: number | undefined = undefined;
    let tokenCount = 0;
    
    stillThinkingTimeout = setTimeout(() => {
      setMessages(prev => {
        const msg = prev.find(m => m.id === assistantMessageId);
        if (msg && !firstTokenReceived && msg.status !== 'streaming') {
          return prev.map(m => 
            m.id === assistantMessageId
              ? { ...m, content: 'Still thinking... taking more time than anticipated.', status: 'still_thinking' as const }
              : m
          );
        }
        return prev;
      });
    }, 2500);

    console.log('[APP] Starting sendMessageStream at', new Date().toISOString());
    console.log('[APP] Message:', messageToSend.substring(0, 50));
    
    try {
      await chatService.sendMessageStream(
        {
          message: messageToSend,
          conversation_id: conversationId || undefined,
          language: selectedLanguage,
          framework: selectedFramework,
        },
        // onChunk - update message as chunks arrive
        (chunk) => {
          console.log('[APP] Received chunk:', chunk.type, chunk.text || chunk.message?.substring(0, 50) || '');
          
          if (chunk.type === 'conversation_id') {
            setConversationId(chunk.conversation_id);
          } else if (chunk.type === 'timing') {
            // Track time to first token
            if (chunk.stage === 'first_token' && chunk.time) {
              timeToFirstToken = chunk.time;
              firstTokenReceived = true;
              if (stillThinkingTimeout) {
                clearTimeout(stillThinkingTimeout);
                stillThinkingTimeout = null;
              }
            }
          } else if (chunk.type === 'content') {
            // Mark first token received
            if (!firstTokenReceived) {
              firstTokenReceived = true;
              if (stillThinkingTimeout) {
                clearTimeout(stillThinkingTimeout);
                stillThinkingTimeout = null;
              }
            }
            
            tokenCount++;
            const chunkText = chunk.text || '';
            
            // Update streaming stats
            setStreamingStats(prev => ({
              chunkCount: prev.chunkCount + 1,
              lastChunkTime: new Date().toLocaleTimeString()
            }));
            
            // Force immediate React update using flushSync to bypass batching
            flushSync(() => {
              setMessages(prev => {
                const updated = prev.map(msg => {
                  if (msg.id === assistantMessageId) {
                    // If this is the first content chunk, replace status message with actual content
                    let currentContent = msg.content;
                    if (currentContent === 'Thinking...' || 
                        currentContent === 'Still thinking... taking more time than anticipated.' ||
                        currentContent.startsWith('Still thinking')) {
                      currentContent = '';
                    }
                    // Remove any previous token summary lines (lines starting with "---" or containing "token")
                    const lines = currentContent.split('\n');
                    const contentLines = lines.filter(line => 
                      !line.includes('token') && 
                      !line.includes('Token') && 
                      !line.trim().startsWith('---')
                    );
                    currentContent = contentLines.join('\n');
                    
                    const newContent = currentContent + chunkText;
                    
                    return { 
                      ...msg, 
                      content: newContent,
                      status: 'streaming' as const,
                      timingInfo: {
                        ...msg.timingInfo,
                        timeToFirstToken,
                        tokenCount,
                      },
                    };
                  }
                  return msg;
                });
                return updated;
              });
              setRenderKey(prev => prev + 1);
            });
            
            // Force immediate scroll update
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 0);
          } else if (chunk.type === 'done') {
            // Clear any pending timeout
            if (stillThinkingTimeout) {
              clearTimeout(stillThinkingTimeout);
            }
            
            const totalTime = (Date.now() - requestStartTime) / 1000;
            
            setMessages(prev => prev.map(msg => 
              msg.id === assistantMessageId
                ? { 
                    ...msg, 
                    content: chunk.message || msg.content,
                    codeSnippets: chunk.code_snippets || [],
                    status: 'complete' as const,
                    timingInfo: {
                      timeToFirstToken: timeToFirstToken || msg.timingInfo?.timeToFirstToken,
                      totalTime,
                      tokenCount: tokenCount || msg.timingInfo?.tokenCount,
                    }
                  }
                : msg
            ));
          }
        },
        // onComplete - finalize message with timing info
        (response) => {
          // Clear any pending timeout
          if (stillThinkingTimeout) {
            clearTimeout(stillThinkingTimeout);
          }
          
          const totalTime = (Date.now() - requestStartTime) / 1000;
          
          // Clean up content - remove any status messages or token summaries
          let finalContent = response.message;
          const lines = finalContent.split('\n');
          const contentLines = lines.filter(line => 
            !line.includes('token') && 
            !line.includes('Token') && 
            !line.trim().startsWith('---') &&
            line !== 'Thinking...' &&
            !line.includes('Still thinking')
          );
          finalContent = contentLines.join('\n').trim();
          
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId
              ? { 
                  ...msg, 
                  content: finalContent,
                  codeSnippets: response.code_snippets || [],
                  status: 'complete',
                  timingInfo: {
                    timeToFirstToken: timeToFirstToken || msg.timingInfo?.timeToFirstToken,
                    totalTime,
                    tokenCount: tokenCount || msg.timingInfo?.tokenCount,
                  }
                }
              : msg
          ));
          if (response.conversation_id) {
            setConversationId(response.conversation_id);
          }
          setIsLoading(false);
        },
        // onError - handle errors
        (error) => {
          // Clear any pending timeout
          if (stillThinkingTimeout) {
            clearTimeout(stillThinkingTimeout);
          }
          
          console.error('Error sending message:', error);
          let errorContent = 'Sorry, I encountered an error while processing your request. Please try again.';
          
          // Provide more specific error messages when possible
          if (error.message.includes('HTTP error')) {
            errorContent = 'Sorry, there was a problem connecting to the server. Please check your connection and try again.';
          } else if (error.message.includes('JSON')) {
            errorContent = 'Sorry, there was a problem processing your message. Please try rephrasing your question.';
          }
          
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId
              ? { 
                  ...msg, 
                  content: errorContent,
                  isError: true,
                  status: 'complete'
                }
              : msg
          ));
          setIsLoading(false);
        }
      );
    } catch (error) {
      // Clear any pending timeout
      if (stillThinkingTimeout) {
        clearTimeout(stillThinkingTimeout);
      }
      
      console.error('Error initiating stream:', error);
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId
          ? { 
              ...msg, 
              content: 'Sorry, I encountered an error while processing your request. Please try again.',
              isError: true,
              status: 'complete'
            }
          : msg
      ));
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setConversationId(undefined);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
        <ToastContainer />
        {/* Sidebar */}
        <Paper
          elevation={1}
          sx={{
            width: isMobile ? '100%' : 320,
            display: 'flex',
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <BotIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" component="h1" fontWeight="bold">
                CSIPay AI
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Pyxis Payment API Assistant
            </Typography>
          </Box>

          <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Programming Language</InputLabel>
              <Select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                label="Programming Language"
              >
                <MenuItem value="python">Python</MenuItem>
                <MenuItem value="javascript">JavaScript</MenuItem>
                <MenuItem value="typescript">TypeScript</MenuItem>
                <MenuItem value="csharp">C#</MenuItem>
                <MenuItem value="dotnet">.NET</MenuItem>
                <MenuItem value="java">Java</MenuItem>
                <MenuItem value="php">PHP</MenuItem>
                <MenuItem value="ruby">Ruby</MenuItem>
                <MenuItem value="go">Go</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Framework (Optional)"
              value={selectedFramework}
              onChange={(e) => setSelectedFramework(e.target.value)}
              placeholder="e.g., FastAPI, Express, Spring"
            />

            <Button
              variant="outlined"
              onClick={clearConversation}
              sx={{ mt: 2 }}
            >
              Clear Conversation
            </Button>
          </Box>

          <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BookIcon sx={{ fontSize: 20 }} />
              <Typography
                component="a"
                href="https://csipay.atlassian.net/wiki/spaces/CAD/pages/2949129/Pyxis+Payment+API+User+Reference"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                API Documentation
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Main Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" component="h2" fontWeight="semibold">
                  Chat with AI Assistant
                  {isLoading && streamingStats.chunkCount > 0 && (
                    <Chip 
                      label={`Streaming: ${streamingStats.chunkCount} chunks`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Ask questions about Pyxis Payment API and get code examples
                </Typography>
              </Box>
              <Chip
                label={`${selectedLanguage} ${selectedFramework ? `+ ${selectedFramework}` : ''}`}
                variant="outlined"
                size="medium"
              />
            </Box>
          </Paper>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                  <MessageIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                  <Typography variant="h5" component="h3" fontWeight="medium" sx={{ mb: 2 }}>
                    Welcome to CSIPay AI Agent
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    I'm here to help you with Pyxis Payment API integration. 
                    Ask me about endpoints, authentication, code examples, or any API-related questions.
                  </Typography>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Try asking:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="text"
                        onClick={() => setInputMessage("How do I authenticate with the Pyxis API?")}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        • How do I authenticate with the Pyxis API?
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => setInputMessage("Show me a code example for processing a sale transaction")}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        • Show me a code example for processing a sale transaction
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => setInputMessage("Show me a code example for processing a refund transaction")}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        • Show me a code example for processing a refund transaction
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => setInputMessage("What are the available API endpoints?")}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        • What are the available API endpoints?
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              messages.map((message) => (
                <ChatMessage key={`${message.id}-${message.content.length}-${renderKey}`} message={message} />
              ))
            )}
            
            {isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BotIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Paper
                  sx={{
                    p: 3,
                    flex: 1,
                    maxWidth: '80%',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '0.2s',
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '0.4s',
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about Pyxis Payment API, request code examples, or get help with integration..."
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default App; 